import React, { useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import AuthImage from "@/components/AuthImage";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Loader2, Trash2, Wand2, LayoutTemplate } from "lucide-react";
import { toast } from "sonner";

export default function TemplateManager({ templates, onChanged, onOpenStudio }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("name", name.trim() || file.name.replace(/\.[^.]+$/, "")); fd.append("file", file);
    try {
      const { data } = await api.post("/fliers/templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Flier uploaded — now place the photo, name and date in the Studio");
      setName(""); onChanged(); onOpenStudio(data.template.id);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/fliers/templates/${t.id}`); toast.success("Template deleted"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
      <Card className="gold-ring glass h-fit p-5" data-testid="template-upload-card">
        <div className="flex items-center gap-2"><LayoutTemplate className="h-5 w-5 text-[hsl(var(--gold))]" /><h3 className="font-display text-lg font-700">Our flier</h3></div>
        <p className="mt-1 text-xs text-muted-foreground">Upload the organisation's birthday flier as it is (PNG/JPG). The Studio then drops the celebrant's photo into the circle and prints his name and date exactly where you place them.</p>
        <Label className="mt-4 block text-xs">Name (optional)</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Galleon Gold" className="mt-1" data-testid="template-name-input" />
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => upload(e.target.files?.[0])} data-testid="template-file-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={busy} className="mt-3 w-full bg-[hsl(var(--gold))] font-700 text-[hsl(var(--primary-foreground))]" data-testid="template-upload-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="mr-2 h-4 w-4" />Upload flier image</>}
        </Button>
      </Card>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-2" data-testid="templates-list">
        {templates.length === 0 && <Card className="gold-ring glass p-8 text-center text-sm text-muted-foreground sm:col-span-2" data-testid="templates-empty">No flier uploaded yet — upload the organisation's birthday flier to begin.</Card>}
        {templates.map((t) => (
          <Card key={t.id} className="gold-ring glass overflow-hidden" data-testid={`template-card-${t.id}`}>
            <div className="aspect-[4/3] bg-black/20"><AuthImage src={`/fliers/templates/${t.id}/image`} alt={t.name} className="h-full w-full object-contain" /></div>
            <div className="p-4">
              <p className="truncate font-display text-base font-700">{t.name}</p>
              <p className="text-xs text-muted-foreground">{t.width}×{t.height}px · {t.frigate || "Galleon"} · {t.created_by}</p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" className="flex-1 bg-[hsl(var(--gold))] font-700 text-[hsl(var(--primary-foreground))]" onClick={() => onOpenStudio(t.id)} data-testid={`template-zones-${t.id}`}><Wand2 className="mr-1.5 h-4 w-4" />Open in Studio</Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(t)} data-testid={`template-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
